import { rtkApi } from '@/shared/api/rtkApi';
import { User } from '@/shared/model/types/table';

interface PatchUser {
  id: number;
  data: Partial<Record<keyof User, string | boolean | null>>;
}

interface PatchUserDataResponse {
  status: string;
}

interface LogEditor {
  id: number;
  lastName: string | null;
  firstName: string | null;
  patronymic: string | null;
}

export interface UserChangeLogItem {
  id: number;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  type: string | null;
  reason: string | null;
  editor: LogEditor | null;
  createdAt: string | null;
}

export interface AllServerHistoryItem {
  privateServerId: string | null;
  name: string | null;
  ip: string | null;
  port: number | null;
  createdAt: string | null;
  closedAt: string | null;
  isActive: boolean | null;
  totalTimeSeconds: number | null;
  maxPlayers: number | null;
  creatorLogin: string | null;
}

export interface AllServersHistoryResponse {
  items: AllServerHistoryItem[];
  totalSeconds: number;
}

const userApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getUserListData: build.query<User[], void>({
      query: () => `/v1/superadmin/user/list`,
      providesTags: ['UsualTable', 'ClubTable'],
    }),
    patchUserData: build.mutation<PatchUserDataResponse, PatchUser>({
      query: (data) => ({
        url: `/v1/superadmin/user/${data.id}`,
        method: 'PATCH',
        body: data.data,
      }),
      invalidatesTags: (result, error, { id }) => ['UsualTable', 'ClubTable', { type: 'UserLog', id }],
    }),
    getUserChangeLog: build.query<UserChangeLogItem[], { userId: number; offset: number; limit?: number }>({
      query: ({ userId, offset, limit = 20 }) =>
        `/v1/superadmin/user/${userId}/change-log?offset=${offset}&limit=${limit}`,
      providesTags: (result, error, { userId }) => [{ type: 'UserLog', id: userId }],
      serializeQueryArgs: ({ queryArgs }) => {
        return { userId: queryArgs.userId };
      },
      merge: (currentCache, newItems) => {
        if (newItems.length > 0) {
          const newIds = new Set(newItems.map((item) => item.id));
          const uniqueCurrentCache = currentCache.filter((item) => !newIds.has(item.id));
          uniqueCurrentCache.push(...newItems);
          return uniqueCurrentCache.sort((a, b) => b.id - a.id);
        }
        return currentCache;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.offset !== previousArg?.offset;
      },
    }),
    restoreUser: build.mutation<{ status: string }, number>({
      query: (id) => ({
        url: `/v1/superadmin/user/${id}/restore`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => ['UsualTable', { type: 'UserLog', id }],
    }),
    getAllServersHistory: build.query<
      AllServersHistoryResponse,
      { startAt?: string; endAt?: string; timezone?: string; limit?: number; offset?: number }
    >({
      query: ({ startAt, endAt, timezone, limit, offset }) => {
        const params: string[] = [];
        if (startAt) params.push(`startAt=${encodeURIComponent(startAt)}`);
        if (endAt) params.push(`endAt=${encodeURIComponent(endAt)}`);
        if (timezone) params.push(`timezone=${encodeURIComponent(timezone)}`);
        if (typeof limit === 'number') params.push(`limit=${limit}`);
        if (typeof offset === 'number') params.push(`offset=${offset}`);
        const qs = params.length ? `?${params.join('&')}` : '';
        return `/v1/superadmin/servers-history${qs}`;
      },
    }),
  }),
});

export const {
  useGetUserListDataQuery,
  usePatchUserDataMutation,
  useGetUserChangeLogQuery,
  useRestoreUserMutation,
  useGetAllServersHistoryQuery,
} = userApi;
